import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type FontFamily = 'system' | 'serif' | 'sans-serif';

// 點擊區域佈局模式
export type TapLayout = 'leftRight-A' | 'leftRight-B' | 'topBottom';

export const TAP_LAYOUT_CONFIG: Record<TapLayout, {
  label: string;
  description: string;
}> = {
  'leftRight-B': { label: '左右 B', description: '左=上一頁，右=下一頁' },
  'leftRight-A': { label: '左右 A', description: '左=下一頁，右=上一頁' },
  'topBottom': { label: '上下', description: '上=上一頁，下=下一頁' },
};

export const FONT_FAMILY_MAP: Record<FontFamily, string> = {
  'system': 'system-ui, -apple-system, "Segoe UI", sans-serif',
  'serif': '"Noto Serif TC", "Source Han Serif TC", "Songti SC", serif',
  'sans-serif': '"Noto Sans TC", "Source Han Sans TC", "PingFang TC", sans-serif',
};

interface ReaderSettings {
  fontSize: number;
  lineHeight: number;
  theme: 'light' | 'dark' | 'sepia';
  fontFamily: FontFamily;
  showTapHint: boolean;
  tapNavigationEnabled: boolean;
  swipeNavigationEnabled: boolean;
  paragraphSpacing: number;
  tapLayout: TapLayout;
}

interface ReaderStore extends ReaderSettings {
  setFontSize: (size: number) => void;
  setLineHeight: (height: number) => void;
  setTheme: (theme: ReaderSettings['theme']) => void;
  setFontFamily: (family: FontFamily) => void;
  setShowTapHint: (show: boolean) => void;
  setTapNavigationEnabled: (enabled: boolean) => void;
  setSwipeNavigationEnabled: (enabled: boolean) => void;
  setParagraphSpacing: (spacing: number) => void;
  setTapLayout: (layout: TapLayout) => void;
  dismissTapHint: () => void;
  reset: () => void;
}

const defaultSettings: ReaderSettings = {
  fontSize: 18,
  lineHeight: 2.0,           // 1.8 → 2.0 更舒適
  theme: 'dark',             // light → dark 護眼
  fontFamily: 'serif',       // system → serif 書卷味
  showTapHint: true,
  tapNavigationEnabled: true,
  swipeNavigationEnabled: true,
  paragraphSpacing: 1.2,     // 1.0 → 1.2 更清晰分段
  tapLayout: 'leftRight-B',
};

export const useReaderSettings = create<ReaderStore>()(
  persist(
    (set) => ({
      ...defaultSettings,
      setFontSize: (fontSize) => set({ fontSize }),
      setLineHeight: (lineHeight) => set({ lineHeight }),
      setTheme: (theme) => set({ theme }),
      setFontFamily: (fontFamily) => set({ fontFamily }),
      setShowTapHint: (showTapHint) => set({ showTapHint }),
      setTapNavigationEnabled: (tapNavigationEnabled) => set({ tapNavigationEnabled }),
      setSwipeNavigationEnabled: (swipeNavigationEnabled) => set({ swipeNavigationEnabled }),
      setParagraphSpacing: (paragraphSpacing) => set({ paragraphSpacing }),
      setTapLayout: (tapLayout) => set({ tapLayout }),
      dismissTapHint: () => set({ showTapHint: false }),
      reset: () => set(defaultSettings),
    }),
    {
      name: 'reader-settings',
      version: 5,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;

        if (version < 2) {
          // v1 → v2: 移除舊的 readingMode，新增新欄位
          delete state.readingMode;
          state.showTapHint = defaultSettings.showTapHint;
          state.tapNavigationEnabled = defaultSettings.tapNavigationEnabled;
        }

        if (version < 3) {
          // v2 → v3: 新增滑動翻頁和段落間距
          state.swipeNavigationEnabled = defaultSettings.swipeNavigationEnabled;
          state.paragraphSpacing = defaultSettings.paragraphSpacing;
          // 將舊的 fontFamily 格式轉換為新格式
          if (state.fontFamily === 'system-ui' || !state.fontFamily) {
            state.fontFamily = 'system';
          }
        }

        if (version < 4) {
          // v3 → v4: 新增點擊區域佈局設定
          state.tapLayout = defaultSettings.tapLayout;
        }

        // v4 → v5: 優化預設值 (不強制遷移舊用戶設定，保留他們的偏好)
        // 新預設: lineHeight 2.0, theme dark, fontFamily serif, paragraphSpacing 1.2

        return {
          ...defaultSettings,
          ...state,
        } as ReaderSettings;
      },
    }
  )
);

export function getThemeStyles(theme: ReaderSettings['theme']) {
  switch (theme) {
    case 'dark':
      return {
        background: '#1a1a1a',
        color: '#e0e0e0',
      };
    case 'sepia':
      return {
        background: '#f4ecd8',
        color: '#5c4b37',
      };
    default:
      return {
        background: '#ffffff',
        color: '#333333',
      };
  }
}
